export const LAST_SELECTED_INBOX_CONV_ID_KEY = 'lastInboxDesktopConversationId';
export const CONV_META_PREFIX = 'conversationMeta:';
export const REAL_CONVERSATION_FOUND_EVENT = 'realConversationFound';

export type InboxConversationMeta = { contactName: string; avatarUrl?: string };

export const safeGetSessionItem = (key: string): string | undefined => {
  try {
    return sessionStorage.getItem(key) || undefined;
  } catch {
    return undefined;
  }
};

export const safeSetSessionItem = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

export const safeRemoveSessionItem = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

export const getConversationMetaFromLocationState = (state: unknown): InboxConversationMeta | undefined => {
  if (!state || typeof state !== 'object') return undefined;
  const s = state as Record<string, unknown>;
  const contactName = typeof s.contactName === 'string' ? s.contactName.trim() : '';
  if (!contactName) return undefined;
  const avatarUrl = typeof s.avatarUrl === 'string' ? s.avatarUrl : undefined;
  return { contactName, avatarUrl: avatarUrl || undefined };
};

export const persistConversationMeta = (conversationId: string, meta: InboxConversationMeta | undefined): void => {
  if (!conversationId || !meta?.contactName) return;
  safeSetSessionItem(`${CONV_META_PREFIX}${conversationId}`, JSON.stringify({
    contactName: meta.contactName,
    avatarUrl: meta.avatarUrl || undefined,
  }));
};

export const getConversationMeta = (conversationId: string): InboxConversationMeta | undefined => {
  const raw = safeGetSessionItem(`${CONV_META_PREFIX}${conversationId}`);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<InboxConversationMeta>;
    const contactName = typeof parsed.contactName === 'string' ? parsed.contactName.trim() : '';
    if (!contactName) return undefined;
    const avatarUrl = typeof parsed.avatarUrl === 'string' ? parsed.avatarUrl : undefined;
    return { contactName, avatarUrl: avatarUrl || undefined };
  } catch {
    return undefined;
  }
};

export const persistLastSelectedInboxConversationId = (conversationId: string): void => {
  if (!conversationId) return;
  safeSetSessionItem(LAST_SELECTED_INBOX_CONV_ID_KEY, String(conversationId));
};

export const clearLastSelectedInboxConversationId = (): void => {
  safeRemoveSessionItem(LAST_SELECTED_INBOX_CONV_ID_KEY);
};

export const getLastSelectedInboxConversationId = (): string | undefined => {
  return safeGetSessionItem(LAST_SELECTED_INBOX_CONV_ID_KEY);
};

export const dispatchRealConversationFound = (detail: {
  conversationId: string;
  contactName?: string;
}): void => {
  try {
    window.dispatchEvent(new CustomEvent(REAL_CONVERSATION_FOUND_EVENT, { detail }));
  } catch {
    // ignore
  }
};

export type BaseConversationLike = {
  id: unknown;
  name?: string;
  avatarUrl?: string;
  lastMessage?: string;
  timestamp?: unknown;
  messages?: unknown;
};

export const shouldKeepLocalConversation = (id: string, selectedId?: string): boolean => {
  if (selectedId && id === String(selectedId)) return true;
  if (id.startsWith('temp-')) return true;
  return Boolean(safeGetSessionItem(`${CONV_META_PREFIX}${id}`));
};

export const mergeConversationListsKeepLocalsFirst = <T extends BaseConversationLike>(
  incoming: T[],
  prev: T[],
  selectedId?: string
): T[] => {
  const byId = new Map<string, T>();
  for (const c of incoming) byId.set(String(c.id), c);

  const keptLocals: T[] = [];
  for (const c of prev) {
    const id = String(c.id);
    if (byId.has(id)) continue;
    if (shouldKeepLocalConversation(id, selectedId)) keptLocals.push(c);
  }

  return [...keptLocals, ...incoming];
};

export const mergeConversationListsHydrateAndKeepLocals = <T extends BaseConversationLike>(
  incoming: T[],
  prev: T[],
  selectedId?: string
): T[] => {
  const incomingById = new Map<string, T>();
  for (const c of incoming) incomingById.set(String(c.id), c);

  for (const prevConv of prev) {
    const id = String(prevConv.id);
    const nextConv = incomingById.get(id);
    if (!nextConv) continue;

    const nextName = typeof nextConv.name === 'string' ? nextConv.name.trim() : '';
    const prevName = typeof prevConv.name === 'string' ? prevConv.name : undefined;

    incomingById.set(id, {
      ...nextConv,
      name: nextName ? nextConv.name : prevName,
      avatarUrl: nextConv.avatarUrl || prevConv.avatarUrl,
      lastMessage: nextConv.lastMessage || prevConv.lastMessage,
      timestamp: nextConv.timestamp || prevConv.timestamp,
    });
  }

  const localsToKeep: T[] = [];
  for (const prevConv of prev) {
    const id = String(prevConv.id);
    if (incomingById.has(id)) continue;
    if (shouldKeepLocalConversation(id, selectedId)) localsToKeep.push(prevConv);
  }

  const mergedIncoming: T[] = [];
  for (const c of incoming) {
    const merged = incomingById.get(String(c.id));
    if (merged) mergedIncoming.push(merged);
  }

  return [...localsToKeep, ...mergedIncoming];
};

export const ensurePlaceholderConversation = <T extends BaseConversationLike>(
  prev: T[],
  conversationId: string,
  meta: InboxConversationMeta | undefined,
  factory: (id: string, meta: InboxConversationMeta) => T
): T[] => {
  if (!conversationId || !meta?.contactName) return prev;
  if (prev.some(c => String(c.id) === String(conversationId))) return prev;
  return [factory(conversationId, meta), ...prev];
};
